"use client";

import * as React from "react";
import { Clock } from "lucide-react";
import { addMinutes, format, isSameDay, parse, roundToNearestMinutes } from "date-fns";

import { Popover, PopoverContent, PopoverTrigger } from "../../../../components/ui/popover.tsx";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  is12Hour?: boolean;
  className?: string;
  children?: React.ReactNode;
  popoverClassName?: string;
  restrictBefore?: Date;
  pickedDate?: Date;
}

export function TimePicker({
  value,
  onChange,
  is12Hour = false,
  className,
  children,
  popoverClassName,
  restrictBefore,
  pickedDate,
}: TimePickerProps) {
  const [time, setTime] = React.useState<Date | undefined>(() => {
    return value ? parse(value, "HH:mm", new Date()) : undefined;
  });
  const [isOpen, setIsOpen] = React.useState(false);

  const hours = is12Hour
    ? Array.from({ length: 12 }, (_, i) => i + 1)
    : Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 12 }, (_, i) => i * 5);
  const periods = ["AM", "PM"];

  const isDisabled = (hour: number, minute?: number) => {
    if (!restrictBefore || !isSameDay(pickedDate ?? new Date(), restrictBefore)) return false;

    const currentTime = new Date();
    const restrictTime = new Date(restrictBefore);

    // Add 10 minutes to the current time and round down to the nearest 5 minutes
    const bufferTime = addMinutes(
      roundToNearestMinutes(currentTime, {
        roundingMethod: "floor",
        nearestTo: 5,
      }),
      10,
    );

    // Convert 12-hour format to 24-hour if necessary
    const adjustedHour = is12Hour && time && time.getHours() >= 12 ? (hour % 12) + 12 : hour;

    // Always allow selecting the current hour or later
    // For the current hour, only disable minutes before the buffer time
    if (adjustedHour === restrictTime.getHours() && minute === undefined) {
      return false;
    }
    if (adjustedHour === restrictTime.getHours()) {
      return (minute ?? 0) < bufferTime.getMinutes();
    }
    if (adjustedHour > bufferTime.getHours()) return false;

    // Disable hours before the buffer time
    return adjustedHour < bufferTime.getHours();
  };

  const handleTimeChange = (type: "hour" | "minute" | "period", value: number | string) => {
    const newTime = time ? new Date(time) : new Date();

    if (type === "hour") {
      if (is12Hour) {
        const currentHours = newTime.getHours();
        const isPM = currentHours >= 12;
        const newHours = ((value as number) % 12) + (isPM ? 12 : 0);

        newTime.setHours(newHours);
      } else {
        newTime.setHours(value as number);
      }
    } else if (type === "minute") {
      newTime.setMinutes(value as number);
    } else if (type === "period") {
      const currentHours = newTime.getHours();
      const newHours = value === "PM" ? (currentHours % 12) + 12 : currentHours % 12;

      newTime.setHours(newHours);
    }

    setTime(newTime);
    onChange(format(newTime, "HH:mm"));
  };

  const formatHour = (hour: number) => {
    if (is12Hour) {
      return hour === 0 ? "12" : hour.toString();
    }

    return hour.toString().padStart(2, "0");
  };

  return (
    <Popover modal={true} open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild className={"cursor-pointer"}>
        {children ? (
          children
        ) : (
          <Button
            className={cn(
              "w-full cursor-pointer! justify-start text-left font-normal",
              !time && "text-muted-foreground",
              className,
            )}
            variant="outline"
          >
            <Clock className="mr-2 h-4 w-4" />
            {time ? format(time, is12Hour ? "hh:mm a" : "HH:mm") : <span>--:--</span>}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent align={"start"} className={cn("z-[1502] w-auto p-0", popoverClassName)}>
        <div className="flex divide-x">
          <ScrollArea className="h-80 w-auto">
            <div className="flex flex-col p-2">
              {hours.map((hour) => (
                <Button
                  key={hour}
                  className="w-full"
                  disabled={isDisabled(hour)}
                  variant={
                    time &&
                    (is12Hour ? (time.getHours() % 12 || 12) === hour : time.getHours() === hour)
                      ? "foreground"
                      : "ghost"
                  }
                  onClick={() => handleTimeChange("hour", hour)}
                >
                  {formatHour(hour)}
                </Button>
              ))}
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
          <ScrollArea className="h-80 w-auto">
            <div className="flex flex-col p-2">
              {minutes.map((minute) => (
                <Button
                  key={minute}
                  className="w-full"
                  disabled={isDisabled(time?.getHours() ?? 0, minute)}
                  variant={time && time.getMinutes() === minute ? "foreground" : "ghost"}
                  onClick={() => handleTimeChange("minute", minute)}
                >
                  {minute.toString().padStart(2, "0")}
                </Button>
              ))}
            </div>
            <ScrollBar orientation="vertical" />
          </ScrollArea>
          {is12Hour && (
            <ScrollArea className="h-80 w-auto">
              <div className="flex flex-col p-2">
                {periods.map((period) => (
                  <Button
                    key={period}
                    className="w-full"
                    variant={
                      time && (time.getHours() >= 12 ? "PM" : "AM") === period
                        ? "foreground"
                        : "ghost"
                    }
                    onClick={() => handleTimeChange("period", period)}
                  >
                    {period}
                  </Button>
                ))}
              </div>
              <ScrollBar orientation="vertical" />
            </ScrollArea>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
